import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useWeb3 } from '../contexts/Web3Context';
import { useUser } from '../contexts/UserContext';
import { ethers } from 'ethers';
import { db, auth } from '../firebaseConfig';
import { doc, updateDoc } from 'firebase/firestore';
import { FaWallet } from 'react-icons/fa';

// Modales
import VerificationModal from '../components/VerificationModal';
import DepositModal from '../components/DepositModal';
import WithdrawModal from '../components/WithdrawModal';
import AccountVerifiedModal from '../components/AccountVerifiedModal';
import VideoExplicativo from '../components/VideoExplicativo';

// 1. Importa los logos desde la carpeta de assets
import { SiTether } from 'react-icons/si';
import clpLogo from '../assets/clp.png';

const XCoinStatusView = () => {
  // --- Contextos ---
  const { userData } = useUser();
  const { wallet, contract, web3Logs, addLog, refreshTrigger } = useWeb3(); // Added addLog, refreshTrigger

  // --- Estados Locales ---
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showVerifiedModal, setShowVerifiedModal] = useState(false);

  // Log Viewer State
  const [showLogs, setShowLogs] = useState(false);

  // Estado para validación del monto (Paso 2)
  const [verifyAmountInput, setVerifyAmountInput] = useState('');
  const [verifyError, setVerifyError] = useState('');
  const [verifying, setVerifying] = useState(false);

  // --- Lógica para el Contador Regresivo ---
  // [REMOVED] Launch Countdown - Sale is Active
  const [timeLeft, setTimeLeft] = useState({ días: 0, horas: 0, minutos: 0, segundos: 0 });

  // --- Lógica de Confirmación de Monto (Paso 2) ---
  const handleVerifyAmount = async () => {
    if (!userData || !auth.currentUser) return;
    setVerifying(true);
    setVerifyError('');

    const inputVal = parseInt(verifyAmountInput);
    const correctAmount = userData.verificationAmount; // Oculto en DB, actualizado por Admin
    const currentAttempts = userData.verificationAttempts || 0;

    if (!inputVal || inputVal <= 0) {
      setVerifyError("Ingresa un monto válido.");
      setVerifying(false);
      return;
    }

    // Verificamos si el Admin ya configuró el monto de verificación en la DB
    if (!correctAmount || correctAmount === 0) {
      setVerifyError("Aún no hemos procesado tu depósito. Por favor revisa más tarde.");
      setVerifying(false);
      return;
    }

    const userRef = doc(db, 'usuarios', auth.currentUser.uid);

    if (inputVal === correctAmount) {
      try {
        await updateDoc(userRef, {
          verificationStatus: 'verified',
          verificationAttempts: 0 // Limpieza
        });
        setShowVerifiedModal(true);
      } catch (err) {
        console.error(err);
        setVerifyError("Error al verificar. Intenta de nuevo.");
      }
    } else {
      // MONTO INCORRECTO
      const newAttempts = currentAttempts + 1;

      if (newAttempts >= 2) {
        // FALLÓ 2 VECES -> RESET
        try {
          await updateDoc(userRef, {
            verificationStatus: 'unverified',
            verificationAmount: 0,
            verificationAttempts: 0
          });
          alert("❌ Has fallado los 2 intentos. Tu cuenta ha vuelto a estado 'No Verificado'.\n\nDebes solicitar la verificación nuevamente.");
          setVerifyAmountInput('');
          // Opcional: Cerrar el modal o cambiar vista, el cambio de estado lo hará automáticamente
        } catch (err) { console.error(err); }
      } else {
        // AVISO
        try {
          await updateDoc(userRef, { verificationAttempts: newAttempts });
          setVerifyError(`❌ Monto incorrecto. Te queda ${2 - newAttempts} intento.`);
        } catch (err) { console.error(err); }
      }
    }
    setVerifying(false);
  };

  // --- Market Stats State ---
  const [marketStats, setMarketStats] = useState({
    totalSupply: 2000,
    circulating: 0,
    available: 0,
    progress: 0,
    loading: true
  });

  const [userBalance, setUserBalance] = useState('0');

  // --- Fetch Market Data (Public RPC) ---
  useEffect(() => {
    const fetchMarketData = async () => {
      const RPC_URL = "https://bsc-dataseed.binance.org/";
      // Contracts
      const CX_ADDR = "0x6508AEB49733f5dAAD56621Bc2AC05193F26b53a";
      const CS_ADDR = "0xE72203e517B80E3c85176376ad3f557538A8d118"; // Crowdsale
      const ST_ADDR = "0x03A6bb992c962176A8C5E2963a7aC288Cc5AD7CD"; // Staking
      const BB_ADDR = "0x3257ee32CbB390CB65a3638Aa56E77136F9cd178"; // Buyback

      try {
        const provider = new ethers.JsonRpcProvider(RPC_URL);
        const xcoin = new ethers.Contract(CX_ADDR, ["function totalSupply() view returns (uint256)", "function balanceOf(address) view returns (uint256)", "function owner() view returns (address)"], provider);

        const ownerAddr = await xcoin.owner();

        const [supply, ownerBal, csBal, stBal, bbBal] = await Promise.all([
          xcoin.totalSupply(),
          xcoin.balanceOf(ownerAddr),
          xcoin.balanceOf(CS_ADDR),
          xcoin.balanceOf(ST_ADDR),
          xcoin.balanceOf(BB_ADDR)
        ]);

        const total = parseFloat(ethers.formatUnits(supply, 18));
        const excluded = parseFloat(ethers.formatUnits(ownerBal, 18)) +
          parseFloat(ethers.formatUnits(csBal, 18)) +
          parseFloat(ethers.formatUnits(stBal, 18)) +
          parseFloat(ethers.formatUnits(bbBal, 18));

        const circ = total - excluded;
        // Progress based on Circulating vs Total (or vs Sale Goal? Assuming Total for now as "Distributed")
        // Note: totalTokens was 1000 in legacy, but TotalSupply is 2000. 
        // We use TotalSupply as the 100% mark.
        const prog = (circ / total) * 100;

        // Available could be interpreted as "What's left in Crowdsale" or "Total - Circulating".
        // Let's use "Total - Circulating" (Undistributed)
        const avail = total - circ;

        setMarketStats({
          totalSupply: total,
          circulating: circ,
          available: avail,
          progress: prog,
          loading: false
        });

      } catch (err) {
        console.error("Market Stats Error:", err);
      }
    };

    fetchMarketData();
  }, []);

  // --- Fetch User Balance ---
  useEffect(() => {
    const fetchUserBal = async () => {
      if (contract && wallet) {
        try {
          const walletAddr = wallet.accounts[0].address;
          addLog(`[BalanceDebug] Fetching for ${walletAddr.substring(0, 8)}...`);

          const bal = await contract.balanceOf(walletAddr);
          addLog(`[BalanceDebug] Raw Balance: ${bal.toString()}`);

          setUserBalance(ethers.formatUnits(bal, 18));
        } catch (e) {
          console.error('[BalanceError]', e);
          addLog(`[BalanceError] ${e.message}`);
        }
      }
    };
    fetchUserBal();
  }, [contract, wallet, refreshTrigger]); // Added refreshTrigger dependency

  // --- RENDERIZADO DEL BOTÓN DE ACCIÓN (CLP) ---
  const renderActionSection = () => {
    if (!userData) return <p>Cargando información de usuario...</p>;

    const status = userData.verificationStatus; // 'unverified', 'pending', 'verified'

    if (status === 'verified') {
      return (
        <div className="card" style={{ marginTop: '20px', border: '1px solid #4caf50' }}>
          <h4 style={{ color: '#4caf50' }}>Cuenta Verificada (CLP)</h4>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <span>Saldo Disponible:</span>
            <strong style={{ fontSize: '1.2rem' }}>Consulta tu saldo en el panel de administración</strong>
          </div>
          <div className="wallet-actions" style={{ display: 'flex', gap: '10px' }}>
            <button className="btn btn-primary" onClick={() => setShowDepositModal(true)} style={{ flex: 1 }}>
              Depositar CLP
            </button>
            <button className="btn btn-secondary" onClick={() => setShowWithdrawModal(true)} style={{ flex: 1, background: '#ff9800', border: 'none' }}>
              Retirar CLP
            </button>
          </div>
        </div>
      );
    } else if (status === 'pending') {
      return (
        <div className="card" style={{ marginTop: '20px', border: '1px solid #ff9800' }}>
          <h4 style={{ color: '#ff9800' }}>Verificación en Proceso</h4>
          <p style={{ fontSize: '0.9rem' }}>
            Hemos recibido tu solicitud. Te enviaremos (o ya enviamos) un depósito de prueba entre $10 y $100 CLP.
            Revisa tu cuenta y confirma el monto exacto abajo.
          </p>
          <div style={{ marginTop: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Monto Recibido ($)</label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="number"
                placeholder="Ej: 50"
                value={verifyAmountInput}
                onChange={(e) => setVerifyAmountInput(e.target.value)}
                style={{ flex: 1, padding: '10px', borderRadius: '5px', border: '1px solid #444', background: '#222', color: 'white' }}
              />
              <button
                onClick={handleVerifyAmount}
                disabled={verifying}
                style={{ padding: '0 20px', background: '#4caf50', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
              >
                {verifying ? '...' : 'Validar'}
              </button>
            </div>
            {verifyError && <p style={{ color: '#ff5252', fontSize: '0.85rem', marginTop: '5px' }}>{verifyError}</p>}
          </div>
        </div>
      );
    } else {
      // Default: En todas las otras situaciones (null, undefined, 'unverified') -> Mostrar botón Verificar
      return (
        <div className="card" style={{ marginTop: '20px', textAlign: 'center' }}>
          <h4>Habilitar Depósitos/Retiros (CLP)</h4>
          <p style={{ fontSize: '0.9rem', marginBottom: '15px', padding: '0 10px' }}>
            Para operar con Pesos Chilenos (CLP), necesitamos verificar tu cuenta bancaria personal.
          </p>
          <button
            className="btn btn-primary"
            onClick={() => setShowVerifyModal(true)}
            style={{ width: '100%', maxWidth: '300px' }}
          >
            Verificar Cuenta Bancaria
          </button>
        </div>
      );
    }
  };

  return (
    <div className="xcoin-status-view">
      <div className="card" style={{ borderTop: '4px solid #00d2ff' }}>
        <h3 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Progreso de Distribución XCoin</span>
          {marketStats.loading && <small style={{ fontSize: '0.8rem', color: '#888' }}>Actualizando...</small>}
        </h3>

        <div className="progress-bar-container" style={{ height: '24px', background: 'rgba(255,255,255,0.1)', borderRadius: '12px', overflow: 'hidden', margin: '20px 0' }}>
          <div
            className="progress-bar"
            style={{
              width: `${marketStats.progress}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #00d2ff, #007bff)',
              transition: 'width 1s ease-in-out',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              paddingRight: '10px'
            }}
          >
            {marketStats.progress > 5 && <span style={{ fontSize: '0.8rem', fontWeight: 'bold', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>{marketStats.progress.toFixed(1)}%</span>}
          </div>
        </div>

        <div className="progress-labels" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', marginBottom: '10px' }}>
          <div style={{ textAlign: 'left' }}>
            <span style={{ color: '#00d2ff', display: 'block', fontWeight: 'bold' }}>En Circulación (Clientes)</span>
            <span style={{ fontSize: '1.2rem' }}>{marketStats.circulating.toLocaleString(undefined, { maximumFractionDigits: 1 })} XCO</span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ color: '#aaa', display: 'block' }}>Por Distribuir / Reservas</span>
            <span style={{ fontSize: '1.2rem' }}>{marketStats.available.toLocaleString(undefined, { maximumFractionDigits: 0 })} XCO</span>
          </div>
        </div>

        <div className="progress-total" style={{ borderTop: '1px solid #333', paddingTop: '10px', marginTop: '10px', textAlign: 'center', color: '#666' }}>
          <small>Suministro Total Blockchain: <strong>{marketStats.totalSupply.toLocaleString()} XCO</strong></small>
        </div>

        {wallet && (
          <div style={{
            marginTop: '20px',
            padding: '15px',
            background: 'linear-gradient(135deg, rgba(0,210,255,0.1), rgba(0,0,0,0))',
            borderRadius: '10px',
            border: '1px solid rgba(0,210,255,0.2)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ color: '#00d2ff', fontWeight: 'bold' }}><FaWallet style={{ marginRight: '8px' }} /> Tu Balance</span>
            <span style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#fff' }}>{parseFloat(userBalance).toLocaleString()} XCO</span>
          </div>
        )}
      </div>

      <div className="countdown-timer card" style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', textAlign: 'center' }}>
        <h4 style={{ color: '#00d2ff' }}>¡Venta Pública Activa!</h4>
        <p style={{ color: '#fff' }}>Adquiere XCoin (XCO) ahora mismo y comienza a recibir dividendos.</p>
        <div style={{ marginTop: '15px' }}>
          <VideoExplicativo videoId="XbBSDqjS2gc" titulo="Ver video explicativo" />
        </div>
      </div>

      <div className="details-row">
        <div className="card">
          <h4>Precio del Token (Serie A)</h4>
          <p className="token-price">10 USDT</p>
          <small className="bonus-phase">Dividendo Mensual desde 2% hasta 3.5%</small>
        </div>
        <div className="card">
          <h4>Monedas Aceptadas</h4>
          <div className="accepted-currencies">
            <div className="currency-item">
              <SiTether size={32} color="#26A17B" title="USDT (Tether)" />
              <span>USDT</span>
            </div>
            <div className="currency-item">
              <img src={clpLogo} alt="Peso Chileno" title="Peso Chileno (CLP)" style={{ width: '32px', height: '32px' }} />
              <span>CLP</span>
            </div>
          </div>
        </div>
      </div>

      {/* SECCIÓN DE ACCIÓN DINÁMICA (VERIFICACIÓN / BOTONES) */}
      <div className="cta-container" style={{ marginTop: '20px' }}>
        {renderActionSection()}
      </div>

      {/* Footer CTA Original (Desactivado visualmente si no es launch, pero mantenemos por diseño si se quiere) */}
      {/* Footer CTA Original (REMOVED Launch Message) */}

      {/* LOG VIEWER TRIGGER (DEBUG) - FLOATING BUTTON - HIDDEN FOR PRODUCTION (set to true to enable) */}
      {false && (
        <div style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 9999 }}>
          <button
            onClick={() => setShowLogs(!showLogs)}
            style={{
              background: 'red',
              color: 'white',
              border: '2px solid white',
              fontSize: '1rem',
              fontWeight: 'bold',
              padding: '10px 20px',
              borderRadius: '30px',
              cursor: 'pointer',
              boxShadow: '0 4px 10px rgba(0,0,0,0.5)'
            }}
          >
            {showLogs ? 'Ocultar Logs' : '🐞 VER LOGS'}
          </button>
        </div>
      )}

      {/* LOGS CONSOLE - FLOATING WINDOW - HIDDEN FOR PRODUCTION */}
      {false && showLogs && (
        <div style={{
          position: 'fixed',
          bottom: '80px',
          right: '20px',
          width: '90%',
          maxWidth: '600px',
          height: '300px',
          background: 'rgba(0,0,0,0.95)',
          color: '#0f0',
          fontFamily: 'monospace',
          fontSize: '0.8rem',
          padding: '15px',
          borderRadius: '10px',
          border: '1px solid #0f0',
          boxShadow: '0 4px 20px rgba(0,0,0,0.8)',
          overflowY: 'auto',
          zIndex: 9998
        }}>
          <div style={{ fontWeight: 'bold', borderBottom: '1px solid #333', marginBottom: '10px', paddingBottom: '5px' }}>
            Web3 Debug Console
            <span style={{ float: 'right', cursor: 'pointer' }} onClick={() => setShowLogs(false)}>✖</span>
          </div>
          {web3Logs && web3Logs.length > 0 ? (
            web3Logs.map((log, idx) => (
              <div key={idx} style={{ marginBottom: '4px', borderBottom: '1px solid #111' }}>
                [{new Date(log.timestamp).toLocaleTimeString()}] {log.message}
              </div>
            ))
          ) : (
            <div style={{ color: '#777' }}>Esperando eventos... (Interactúa con la wallet)</div>
          )}
        </div>
      )}

      {/* MODALES */}
      <VerificationModal isOpen={showVerifyModal} onClose={() => setShowVerifyModal(false)} />
      <DepositModal isOpen={showDepositModal} onClose={() => setShowDepositModal(false)} />
      <WithdrawModal isOpen={showWithdrawModal} onClose={() => setShowWithdrawModal(false)} />
      <AccountVerifiedModal isOpen={showVerifiedModal} onClose={() => setShowVerifiedModal(false)} />

    </div>
  );
};

export default XCoinStatusView;